import { ArrowDropDown, ArrowDropUp, UnfoldMore } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { COLORS } from "assets";
import { ReactNode } from "react";
import { IColumn, IDataTable } from "types";

const DataTable = <T extends {}, U = string>({
  rows,
  columns,
  isLoading,
  error,
  sort,
  isAscending,
  onSort,
  selectedIds,
  getRowId
}: IDataTable<T, U>) => {
  const getSortedValue = (column: IColumn<T, U>, sort?: U, isAscending?: boolean) =>
    sort && sort === column.sort ? (isAscending ? "asce" : "desc") : !column.sort ? false : "none";

  const selectedSet = selectedIds?.length ? new Set(selectedIds) : null;

  return (
    <TableContainer
      sx={{
        width: "100%",
        "::-webkit-scrollbar": {
          backgroundColor: "white",
          height: 10,
          borderRadius: 10,
          cursor: "pointer",
          border: 0
        },
        "::-webkit-scrollbar-track": {
          backgroundColor: "transparent",
          borderRadius: 10,
          cursor: "pointer",
          border: 0
        },
        "::-webkit-scrollbar-thumb": {
          backgroundColor: "text.secondary",
          borderRadius: 10,
          cursor: "pointer",
          border: 0
        }
      }}
    >
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow>
            {columns.map((column, i) => {
              if (typeof column === "boolean") return null;
              const sortDir = getSortedValue(column, sort, isAscending);
              const { header, accessor, sort: colSort, ...cellProps } = column;
              return (
                <TableCell
                  sx={{
                    border: 0,
                    bgcolor: COLORS.surface.tableHeader,
                    py: 1.5,
                    borderBottom: `1px solid ${COLORS.surface.border}`
                  }}
                  {...cellProps}
                  key={i}
                >
                  <Box
                    display="flex"
                    minWidth="max-content"
                    alignItems="center"
                    justifyContent={
                      column.align === "right" || column.align === "justify"
                        ? "flex-end"
                        : column.align === "center"
                          ? "center"
                          : "flex-start"
                    }
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: COLORS.surface.primaryText, pr: 1, fontSize: 13, fontWeight: 500 }}
                    >
                      {header}
                    </Typography>
                    <IconButton
                      onClick={() => onSort && column.sort && onSort(column.sort)}
                      sx={{ p: 0, fontSize: "14px" }}
                    >
                      {sortDir === "asce" ? (
                        <ArrowDropUp color="primary" fontSize="inherit" />
                      ) : sortDir === "desc" ? (
                        <ArrowDropDown color="primary" fontSize="inherit" />
                      ) : sortDir === "none" ? (
                        <UnfoldMore color="primary" fontSize="inherit" />
                      ) : null}
                    </IconButton>
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>

        <TableBody>
          {rows && rows.length > 0 ? (
            rows.map((row, i) => {
              const rowId = getRowId?.(row);
              const isSelected = Boolean(rowId && selectedSet?.has(rowId));
              return (
                <TableRow
                  key={rowId || i}
                  selected={isSelected}
                  sx={{
                    bgcolor: isSelected ? COLORS.surface.selection : "transparent",
                    transition: "background-color 120ms ease-out",
                    "&.Mui-selected:hover": { bgcolor: COLORS.surface.selection },
                    "&:hover": {
                      bgcolor: isSelected ? COLORS.surface.selection : COLORS.surface.tableHeader
                    }
                  }}
                >
                  {columns.map((column, colIndex) => {
                    if (typeof column === "boolean") return null;
                    const { header, accessor, sort, ...cellProps } = column;
                    return (
                      <TableCell
                        sx={{
                          color: COLORS.surface.primaryText,
                          border: 0,
                          fontSize: 14,
                          borderBottom: `1px solid ${COLORS.surface.border}`,
                          py: 1.5,
                          bgcolor: "inherit"
                        }}
                        {...cellProps}
                        key={colIndex}
                      >
                        {typeof accessor === "function" ? accessor(row, i) : (row[accessor] as ReactNode)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })
          ) : (
            <TableRow sx={{ border: 0 }}>
              <TableCell align="center" colSpan={columns.length}>
                {isLoading ? (
                  <CircularProgress />
                ) : (
                  <Typography variant="subtitle1" color="error" textAlign="center">
                    {error ? error.message || JSON.stringify(error) : "No Records."}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DataTable;
